import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, LogIn, Globe, Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const COMING_SOON_IDS: string[] = [];

interface NavbarProps {
  onDownloadClick?: () => void;
  onComingSoonClick?: () => void;
}

/** Maps a Spanish path to its English equivalent and vice-versa */
const getAlternatePath = (pathname: string, targetLang: 'es' | 'en'): string => {
  if (targetLang === 'en') {
    if (pathname === '/terminos_y_condiciones') return '/en/terms_and_conditions';
    if (pathname === '/politicas-de-privacidad') return '/en/privacy-policy';
    return '/en/';
  } else {
    if (pathname === '/en/terms_and_conditions') return '/terminos_y_condiciones';
    if (pathname === '/en/privacy-policy') return '/politicas-de-privacidad';
    return '/';
  }
};

const Navbar: React.FC<NavbarProps> = ({ onDownloadClick, onComingSoonClick }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState<string>('entrenadores');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'en' ? 'EN' : 'ES';

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const switchLanguage = (lang: 'es' | 'en') => {
    const target = getAlternatePath(location.pathname, lang);
    setLangMenuOpen(false);
    closeMobileMenu();
    navigate(target);
  };

  // Close lang dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navLinks = [
    { id: 'entrenadores', label: t('nav.entrenadores') },
    { id: 'instituciones', label: t('nav.instituciones') },
    { id: 'licencias', label: t('nav.licencias') },
  ];

  const handleNavClick = (linkId: string) => {
    setActiveSection(linkId);
    closeMobileMenu();
  };

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const homeHref = i18n.language === 'en' ? '/en/' : '/';

  return (
    <nav className="sticky top-0 z-50 w-full bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={homeHref} className="flex-shrink-0 flex items-center" aria-label="+Trainer Inicio">
            <img
              src="/logo_trainer.png"
              alt="+Trainer"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </a>

          {/* Center: enlaces */}
          <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
            {navLinks.map((link) =>
              COMING_SOON_IDS.includes(link.id) ? (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => { setActiveSection(link.id); onComingSoonClick?.(); }}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === link.id ? 'text-mas10-primary' : 'text-gray-600 hover:text-mas10-primary'
                  }`}
                >
                  {link.label}
                </button>
              ) : (
                <a
                  key={link.id}
                  href={`${homeHref}#${link.id}`}
                  onClick={() => setActiveSection(link.id)}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === link.id ? 'text-mas10-primary' : 'text-gray-600 hover:text-mas10-primary'
                  }`}
                >
                  {link.label}
                </a>
              )
            )}
          </div>

          {/* Right: idioma + Iniciar Sesión + Descargar App (desktop) */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            {/* Language switcher dropdown */}
            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => setLangMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 text-gray-600 text-sm font-medium hover:text-mas10-primary transition-colors"
                aria-haspopup="listbox"
                aria-expanded={langMenuOpen}
                aria-label={t('nav.language')}
              >
                <Globe className="w-4 h-4" />
                <span>{currentLang}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden z-50"
                    role="listbox"
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={i18n.language === 'es'}
                      onClick={() => switchLanguage('es')}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                        i18n.language === 'es' ? 'text-mas10-primary bg-mas10-primary/5' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      🇦🇷 Español
                    </button>
                    <button
                      type="button"
                      role="option"
                      aria-selected={i18n.language === 'en'}
                      onClick={() => switchLanguage('en')}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                        i18n.language === 'en' ? 'text-mas10-primary bg-mas10-primary/5' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      🇺🇸 English
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="https://plans.mastrainerapp.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-600 text-sm font-medium hover:text-mas10-primary transition-colors"
            >
              <LogIn className="w-4 h-4" />
              {t('nav.login')}
            </a>
            <motion.button
              type="button"
              onClick={onDownloadClick}
              className="flex items-center gap-2 bg-mas10-primary hover:bg-blue-800 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors shadow-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-4 h-4" />
              {t('nav.download')}
            </motion.button>
          </div>

          {/* Mobile: botón hamburguesa */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            aria-label={mobileMenuOpen ? t('nav.close_menu') : t('nav.open_menu')}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" aria-hidden />
            ) : (
              <Menu className="w-6 h-6" aria-hidden />
            )}
          </button>
        </div>
      </div>

      {/* Mobile: menú drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
              onClick={closeMobileMenu}
              aria-hidden
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-xl md:hidden flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.menu')}
            >
              <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-900">{t('nav.menu')}</span>
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                  aria-label={t('nav.close_menu')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-4 px-4">
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link) =>
                    COMING_SOON_IDS.includes(link.id) ? (
                      <button
                        key={link.id}
                        type="button"
                        onClick={() => {
                          setActiveSection(link.id);
                          closeMobileMenu();
                          onComingSoonClick?.();
                        }}
                        className={`py-3 px-3 rounded-lg text-base font-medium transition-colors w-full text-left ${
                          activeSection === link.id ? 'text-mas10-primary bg-mas10-primary/10' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        key={link.id}
                        href={`${homeHref}#${link.id}`}
                        onClick={() => handleNavClick(link.id)}
                        className={`py-3 px-3 rounded-lg text-base font-medium transition-colors ${
                          activeSection === link.id ? 'text-mas10-primary bg-mas10-primary/10' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {link.label}
                      </a>
                    )
                  )}
                </nav>
                <div className="border-t border-gray-100 mt-4 pt-4 flex flex-col gap-1">
                  {/* Mobile language switcher */}
                  <div className="py-2 px-3">
                    <div className="flex items-center gap-2 text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">
                      <Globe className="w-3.5 h-3.5" />
                      {t('nav.language')}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => switchLanguage('es')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${
                          i18n.language === 'es'
                            ? 'bg-mas10-primary text-white border-mas10-primary'
                            : 'text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        🇦🇷 Español
                      </button>
                      <button
                        type="button"
                        onClick={() => switchLanguage('en')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${
                          i18n.language === 'en'
                            ? 'bg-mas10-primary text-white border-mas10-primary'
                            : 'text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        🇺🇸 English
                      </button>
                    </div>
                  </div>

                  <a
                    href="https://plans.mastrainerapp.com/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-2 py-3 px-3 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    {t('nav.login')}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      onDownloadClick?.();
                      closeMobileMenu();
                    }}
                    className="flex items-center gap-2 py-3 px-3 rounded-lg bg-mas10-primary hover:bg-blue-800 text-white font-semibold transition-colors text-left"
                  >
                    <Download className="w-4 h-4" />
                    {t('nav.download')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
